import * as LDClient from 'launchdarkly-js-client-sdk'
import { castFlagValue } from '@/features/feature-toggles/toggle-util'
import { fromEvent, type Observable } from 'rxjs'
import { env } from '@/infra/env'
import type {
  FeatureToggleClient,
  FeatureToggleUser,
  FlagValue,
} from '@/features/feature-toggles/toggle-client'

/**
 * Wrapper around the live LaunchDarkly client.
 */
export class LiveLDClient implements FeatureToggleClient {
  private constructor(private readonly _client: LDClient.LDClient) {}

  /** @inheritDoc */
  variation<T extends FlagValue>(key: string, defaultValue: T): T {
    return castFlagValue(
      this._client.variation(key, defaultValue),
      defaultValue,
    )
  }

  /** @inheritDoc */
  flagChanges(key: string): Observable<unknown> {
    return fromEvent(this._client, `change:${key}`)
  }

  /** @inheritDoc */
  async identify(user: FeatureToggleUser): Promise<void> {
    await this._client.identify({
      key: user.id,
      email: user.email,
      custom: {
        workspace_slugs: user.workspaceSlugs,
      },
    })
  }

  /**
   * Creates a live LD client.
   */
  static create() {
    const anonymousContext: LDClient.LDContext = {
      key: 'anonymous',
      anonymous: true,
    }
    const ldClient = LDClient.initialize(
      env.LAUNCHDARKLY_CLIENT_SIDE_ID,
      anonymousContext,
      {
        bootstrap: window.__ldFlagsState,
      },
    )
    return new LiveLDClient(ldClient)
  }
}
